import Axios from 'src/services/axios';
import { isNil, uniqBy, uniq, partial, isEmpty, isNumber, isBoolean } from 'lodash';
import { ValidMembersData } from 'src/pages/AssortmentStrategy/TargetSetting/Criteria/Criteria.types';
import { getItemFromCache, addItemToCache, getOrAddItemAsyncCache } from './ValidValuesCache';
import { getAllColors } from '../../AssortmentAdd/Assortment.client';
import { ISASSORTMENT, CC_COLOR } from 'src/utils/Domain/Constants';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import queryString from 'query-string';
import { toast } from 'react-toastify';
import ServiceContainer from 'src/ServiceContainer';
import { ListDataApi } from 'configuration-schemas/confdefnView';
import { Suggestion } from 'src/common-ui/components/Inputs/InputSuggest/InputSuggest';

export interface ValidValues {
  id: string;
  name: string;
}

function requestingValidMembers(url: string) {
  return url.includes('validMembers');
}

function requestingDependentValues(url: string) {
  return url.includes('api/v2/dependents'); // probably too specific
}

function mapValidMembers(renderIdOnly: boolean, concatValues: boolean, option: ValidMembersData) {
  if (renderIdOnly) {
    return {
      value: option.id,
      label: option.id,
    };
  }

  return {
    value: option.id,
    label: concatValues ? `${option.id} ${option.name}` : option.name,
  };
}

function mapValidValues(concatValues: boolean, option: ValidMembersData) {
  const labelValue = option.description || option.name;
  const label = concatValues ? `${option.id} ${labelValue}` : labelValue;

  return {
    value: option.id,
    label,
  };
}
export interface ValueLabelPair extends ReturnType<typeof mapValidValues> {}

function mapStringValues(_concatValues: boolean, option: string) {
  return {
    value: option,
    label: option,
  };
}

function getValidMembers(
  url: string,
  insertEmptyOption: boolean,
  concatOptionValues: boolean,
  ignoreCache: boolean,
  renderIdOnly: boolean,
) {
  const cachedData = getItemFromCache(url);
  const mapFn = partial(mapValidMembers, renderIdOnly, concatOptionValues);

  if (!isNil(cachedData) && !ignoreCache) {
    // @ts-ignore
    let options = cachedData.map(mapFn);
    if (!insertEmptyOption) {
      options = options.filter((option) => !isEmpty(option.value) || isNumber(option.value) || isBoolean(option.value));
    }

    return Promise.resolve(options);
  }

  return Axios.get(url).then((resp) => {
    const respInfo = resp.data;
    let values = !isNil(respInfo) ? respInfo : [];
    values = !isNil(respInfo.data) ? respInfo.data : values;

    if (insertEmptyOption) {
      values.push({
        id: '',
        name: '',
      });
    }

    values = uniqBy(values, 'id');
    addItemToCache(url, values);

    const options = values.map(mapFn);
    return options;
  });
}

async function getValues(url: string, insertEmptyOption: boolean, concatOptionValues: boolean, ignoreCache: boolean) {
  // getValidValues
  const allValuesLookup = url.includes('validValues/all');
  const urlParts = url.split('=');
  const valuesType = urlParts[urlParts.length - 1];
  const mapFn = partial(mapValidValues, concatOptionValues);
  const vvReq = async () => {
    const respInfo = (await Axios.get(url)).data;
    let values = allValuesLookup ? respInfo.data[valuesType] : respInfo.data;

    if (insertEmptyOption) {
      values.push({
        id: '',
        name: '',
      });
    }

    values = uniqBy(values, 'id');
    return values;
  };

  if (!ignoreCache) {
    const cachedData = await getOrAddItemAsyncCache(url, vvReq);
    // @ts-ignore
    let options = cachedData.map(mapFn);
    if (!insertEmptyOption) {
      options = options.filter((option) => !isEmpty(option.value) || isNumber(option.value) || isBoolean(option.value));
    }

    return options;
  } else {
    const values = await vvReq();
    const options = values.map(mapFn);
    return options;
  }
}

async function getDependentValues(
  url: string,
  insertEmptyOption: boolean,
  concatOptionValues: boolean,
  ignoreCache = false,
) {
  // parse url query params and get string
  const qs = queryString.parse(url);
  const dependentOnId = qs.id;
  const mapFn = partial(mapStringValues, concatOptionValues);

  const fetchDependents = async () => {
    const respInfo = (await Axios.get(url)).data;
    if (isNil(respInfo?.data) || isNil(dependentOnId)) {
      return [];
    }

    // @ts-ignore
    return respInfo.data[dependentOnId] ?? [];
  };

  const dependentValues = ignoreCache ? await fetchDependents() : await getOrAddItemAsyncCache(url, fetchDependents);
  const values = (dependentValues as any) ?? [];
  let options = !isNil(values) ? values.filter((val: any) => !isEmpty(val?.value) || !isEmpty(val)).map(mapFn) : [];

  if (insertEmptyOption) {
    options.push({
      value: '',
      label: '',
    });
  }

  options = uniqBy(options, (option: ValueLabelPair) => option['value'].toUpperCase());

  return options;
}

// We don't need all the other params for this, since the special behaviors can be controlled
// from the pivot.
export function getListDataValidValues(config: ListDataApi) {
  return ServiceContainer.pivotService
    .listData(config.defnId, undefined, {
      ...config.params,
      nestData: false,
    })
    .then((resp) => {
      const validValues: Suggestion[] = resp.tree as unknown as Suggestion[];
      return validValues;
    });
}

export function getValidValues(
  url: string,
  insertEmptyOption = true,
  concatOptionValues = false,
  ignoreCache = false,
  renderIdOnly = false,
) {
  if (requestingValidMembers(url)) {
    return getValidMembers(url, insertEmptyOption, concatOptionValues, ignoreCache, renderIdOnly);
  } else if (requestingDependentValues(url)) {
    return getDependentValues(url, insertEmptyOption, concatOptionValues, ignoreCache);
  }

  return getValues(url, insertEmptyOption, concatOptionValues, ignoreCache);
}

export function updateLifecycleParams(styleColorId: string, newValue: { [s: string]: any }) {
  const postData = {
    product: styleColorId,
    attributes: { ...newValue },
  };

  return Axios.post('/api/assortment/lifecycleParams?appName=Assortment', postData).catch((err: string) => {
    toast.error('An error occurred attempting to update the life cycle parameters');
    ServiceContainer.loggingService.error(`An error occurred attempting to update the life cycle parameters`, err);
  });
}

export function updateRangingParams(styleColorId: string, newValue: { [s: string]: unknown }) {
  // TODO: inject stylepreview cache clearing here
  const postData = {
    product: styleColorId,
    attributes: { ...newValue },
  };

  return Axios.post('/api/assortment/rangingParams?appName=Assortment', postData).catch((err: string) => {
    toast.error('An error occurred updating the store ranging parameters');
    ServiceContainer.loggingService.error(`An error occurred updating the store ranging parameters`, err);
  });
}

export function removeStyleColor(styleColorId: string) {
  return Axios.post(`/api/assortment/removeFromAssortment`, null, {
    params: {
      appName: 'Assortment',
      productId: styleColorId,
    },
  }).catch((err: string) => {
    toast.error('An error occurs when trying to remove style color');
    ServiceContainer.loggingService.error(`An error occurs when trying to remove style color`, err);
  });
}

export function removeStyleColors(styleColorIds: string[]) {
  return Axios.post(
    `/api/assortment/removeFromAssortment`,
    {
      productIds: styleColorIds,
    },
    {
      params: {
        appName: 'Assortment',
      },
    },
  ).catch((err: string) => {
    toast.error('An error occurs when trying to remove style color');
    ServiceContainer.loggingService.error(`An error occurs when trying to remove style color`, err);
  });
}

export function getExistingColorsInAsst(existingStyleColors: BasicPivotItem[]): string[] {
  return existingStyleColors.filter((i) => i[ISASSORTMENT] === 'true').map((d) => d[CC_COLOR]);
}

export function getExistingColorsNotInAsst(existingStyleColors: BasicPivotItem[]): string[] {
  return existingStyleColors.filter((i) => i[ISASSORTMENT] !== 'true').map((d) => d[CC_COLOR]);
}

export function getAvailableColors(existingStyleColors: BasicPivotItem[], currentStyleColor = '') {
  const existingInAsst = getExistingColorsInAsst(existingStyleColors);
  const mapFn = partial(mapStringValues, false); // concat not applicable here
  return getAllColors()
    .then((colors) => {
      const availableColors = colors.filter((color) => {
        return existingInAsst.indexOf(color) < 0 || color === currentStyleColor;
      });

      return availableColors;
    })
    .then((availableColors) => {
      let values = availableColors;
      values.push('');
      values = uniq(values);

      const options = values.map(mapFn);
      return options;
    });
}
